import { useState } from 'react';
import { useMetricsOverview, useLeaderboard, useCalculateMetrics } from '@/hooks/useMetrics';
import { useFeedbackStats, useProviderAccuracies } from '@/hooks/useFeedback';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import {
  TrendingUp,
  Trophy,
  AlertTriangle,
  Bot,
  ThumbsUp,
  ThumbsDown,
  Target,
  RefreshCw,
  Medal,
  ArrowUp,
  ArrowDown,
  Minus,
} from 'lucide-react';

type TimeRange = 7 | 14 | 30 | 90;

export default function Performance() {
  const [timeRange, setTimeRange] = useState<TimeRange>(30);

  // Fetch data
  const { data: overview, isLoading: overviewLoading } = useMetricsOverview(timeRange);
  const { data: leaderboard, isLoading: leaderboardLoading } = useLeaderboard({
    days: timeRange,
    limit: 10,
  });
  const { data: feedbackStats, isLoading: feedbackLoading } = useFeedbackStats(timeRange);
  const { data: providerAccuracies } = useProviderAccuracies();
  const calculateMutation = useCalculateMetrics();

  const timeRanges: { value: TimeRange; label: string }[] = [
    { value: 7, label: '7 days' },
    { value: 14, label: '14 days' },
    { value: 30, label: '30 days' },
    { value: 90, label: '90 days' },
  ];

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    if (score >= 40) return 'text-orange-600';
    return 'text-red-600';
  };

  const getScoreBg = (score: number) => {
    if (score >= 80) return 'bg-green-100';
    if (score >= 60) return 'bg-yellow-100';
    if (score >= 40) return 'bg-orange-100';
    return 'bg-red-100';
  };

  const getRankIcon = (rank: number) => {
    if (rank === 1) return <Medal className="h-5 w-5 text-yellow-500" />;
    if (rank === 2) return <Medal className="h-5 w-5 text-gray-400" />;
    if (rank === 3) return <Medal className="h-5 w-5 text-amber-700" />;
    return <span className="w-5 text-center font-medium text-muted-foreground">{rank}</span>;
  };

  const getChangeIcon = (change?: 'up' | 'down' | 'same') => {
    if (change === 'up') return <ArrowUp className="h-4 w-4 text-green-500" />;
    if (change === 'down') return <ArrowDown className="h-4 w-4 text-red-500" />;
    return <Minus className="h-4 w-4 text-muted-foreground" />;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Performance Dashboard</h1>
          <p className="text-muted-foreground">
            Developer metrics, leaderboards, and AI feedback analytics
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => calculateMutation.mutate()}
            disabled={calculateMutation.isPending}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${calculateMutation.isPending ? 'animate-spin' : ''}`} />
            Recalculate
          </Button>
          <span className="text-sm text-muted-foreground">Time range:</span>
          {timeRanges.map((range) => (
            <Button
              key={range.value}
              variant={timeRange === range.value ? 'default' : 'outline'}
              size="sm"
              onClick={() => setTimeRange(range.value)}
            >
              {range.label}
            </Button>
          ))}
        </div>
      </div>

      {/* Overview Stats */}
      {overviewLoading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      ) : overview ? (
        <>
          <div className="grid gap-4 md:grid-cols-5">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <TrendingUp className="h-4 w-4" />
                  Avg Overall Score
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className={`text-2xl font-bold ${getScoreColor(overview.summary.avgOverallScore ?? 0)}`}>
                  {(overview.summary.avgOverallScore ?? 0).toFixed(1)}
                </div>
                <p className="text-xs text-muted-foreground">
                  {overview.summary.totalDevelopers} developers
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Activity Score</CardTitle>
              </CardHeader>
              <CardContent>
                <div className={`text-2xl font-bold ${getScoreColor(overview.summary.avgActivityScore ?? 0)}`}>
                  {(overview.summary.avgActivityScore ?? 0).toFixed(1)}
                </div>
                <p className="text-xs text-muted-foreground">Team average</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Quality Score</CardTitle>
              </CardHeader>
              <CardContent>
                <div className={`text-2xl font-bold ${getScoreColor(overview.summary.avgQualityScore ?? 0)}`}>
                  {(overview.summary.avgQualityScore ?? 0).toFixed(1)}
                </div>
                <p className="text-xs text-muted-foreground">Team average</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Responsiveness</CardTitle>
              </CardHeader>
              <CardContent>
                <div className={`text-2xl font-bold ${getScoreColor(overview.summary.avgResponsivenessScore ?? 0)}`}>
                  {(overview.summary.avgResponsivenessScore ?? 0).toFixed(1)}
                </div>
                <p className="text-xs text-muted-foreground">Team average</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-orange-500" />
                  Needs Attention
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-orange-600">
                  {overview.summary.outlierCount}
                </div>
                <p className="text-xs text-muted-foreground">Flagged developers</p>
              </CardContent>
            </Card>
          </div>

          {/* Score Distribution */}
          <Card>
            <CardHeader>
              <CardTitle>Score Distribution</CardTitle>
              <CardDescription>Distribution of developer performance scores</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-5">
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <p className="text-3xl font-bold text-green-600">
                    {overview.scoreDistribution?.excellent ?? 0}
                  </p>
                  <p className="text-sm text-muted-foreground">Excellent (80+)</p>
                </div>
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <p className="text-3xl font-bold text-blue-600">
                    {overview.scoreDistribution?.good ?? 0}
                  </p>
                  <p className="text-sm text-muted-foreground">Good (60-79)</p>
                </div>
                <div className="text-center p-4 bg-yellow-50 rounded-lg">
                  <p className="text-3xl font-bold text-yellow-600">
                    {overview.scoreDistribution?.average ?? 0}
                  </p>
                  <p className="text-sm text-muted-foreground">Average (40-59)</p>
                </div>
                <div className="text-center p-4 bg-orange-50 rounded-lg">
                  <p className="text-3xl font-bold text-orange-600">
                    {overview.scoreDistribution?.belowAverage ?? 0}
                  </p>
                  <p className="text-sm text-muted-foreground">Below Avg (20-39)</p>
                </div>
                <div className="text-center p-4 bg-red-50 rounded-lg">
                  <p className="text-3xl font-bold text-red-600">
                    {overview.scoreDistribution?.poor ?? 0}
                  </p>
                  <p className="text-sm text-muted-foreground">Poor (&lt;20)</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </>
      ) : null}

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Leaderboard */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Trophy className="h-5 w-5 text-yellow-500" />
              Leaderboard
            </CardTitle>
            <CardDescription>Top performing developers</CardDescription>
          </CardHeader>
          <CardContent>
            {leaderboardLoading ? (
              <div className="flex justify-center py-8">
                <LoadingSpinner />
              </div>
            ) : !leaderboard?.length ? (
              <p className="text-center text-muted-foreground py-8">
                No leaderboard data available
              </p>
            ) : (
              <div className="space-y-3">
                {leaderboard.map((entry) => (
                  <div
                    key={entry.userId}
                    className={`flex items-center gap-3 p-3 rounded-lg ${
                      entry.rank <= 3 ? 'bg-muted/50' : ''
                    }`}
                  >
                    <div className="flex items-center justify-center w-8">
                      {getRankIcon(entry.rank)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{entry.displayName}</p>
                      <p className="text-xs text-muted-foreground">@{entry.username}</p>
                    </div>
                    <div className="text-right">
                      <div className={`font-bold ${getScoreColor(entry.overallScore ?? 0)}`}>
                        {(entry.overallScore ?? 0).toFixed(1)}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span>{entry.commitCount} commits</span>
                        {getChangeIcon(entry.change)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Needs Attention */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-orange-500" />
              Needs Attention
            </CardTitle>
            <CardDescription>Developers with flagged metrics</CardDescription>
          </CardHeader>
          <CardContent>
            {overviewLoading ? (
              <div className="flex justify-center py-8">
                <LoadingSpinner />
              </div>
            ) : !overview?.needsAttention?.length ? (
              <p className="text-center text-muted-foreground py-8">
                No developers flagged for attention
              </p>
            ) : (
              <div className="space-y-3">
                {overview.needsAttention.map((dev) => (
                  <div key={dev.userId} className="p-3 rounded-lg bg-orange-50 border border-orange-100">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <p className="font-medium">{dev.displayName}</p>
                        <p className="text-xs text-muted-foreground">@{dev.username}</p>
                      </div>
                      <span className={`px-2 py-1 rounded text-sm font-medium ${getScoreBg(dev.overallScore ?? 0)} ${getScoreColor(dev.overallScore ?? 0)}`}>
                        {(dev.overallScore ?? 0).toFixed(1)}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {dev.reasons.map((reason, idx) => (
                        <span
                          key={idx}
                          className="px-2 py-0.5 bg-orange-100 text-orange-800 rounded text-xs"
                        >
                          {reason}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* AI Feedback Section */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Feedback Stats */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bot className="h-5 w-5" />
              AI Feedback Overview
            </CardTitle>
            <CardDescription>User feedback on AI responses</CardDescription>
          </CardHeader>
          <CardContent>
            {feedbackLoading ? (
              <div className="flex justify-center py-8">
                <LoadingSpinner />
              </div>
            ) : feedbackStats ? (
              <div className="space-y-6">
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-3 bg-green-50 rounded-lg">
                    <ThumbsUp className="h-6 w-6 mx-auto mb-1 text-green-600" />
                    <p className="text-2xl font-bold text-green-600">
                      {feedbackStats.byType.positive}
                    </p>
                    <p className="text-xs text-muted-foreground">Positive</p>
                  </div>
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <Minus className="h-6 w-6 mx-auto mb-1 text-gray-600" />
                    <p className="text-2xl font-bold text-gray-600">
                      {feedbackStats.byType.neutral}
                    </p>
                    <p className="text-xs text-muted-foreground">Neutral</p>
                  </div>
                  <div className="text-center p-3 bg-red-50 rounded-lg">
                    <ThumbsDown className="h-6 w-6 mx-auto mb-1 text-red-600" />
                    <p className="text-2xl font-bold text-red-600">
                      {feedbackStats.byType.negative}
                    </p>
                    <p className="text-xs text-muted-foreground">Negative</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-4 bg-muted/50 rounded-lg">
                    <Target className="h-6 w-6 mx-auto mb-1 text-primary" />
                    <p className="text-2xl font-bold">
                      {(feedbackStats.accuracyRate ?? 0).toFixed(1)}%
                    </p>
                    <p className="text-sm text-muted-foreground">Accuracy Rate</p>
                  </div>
                  <div className="text-center p-4 bg-muted/50 rounded-lg">
                    <ThumbsUp className="h-6 w-6 mx-auto mb-1 text-primary" />
                    <p className="text-2xl font-bold">
                      {(feedbackStats.satisfactionRate ?? 0).toFixed(1)}%
                    </p>
                    <p className="text-sm text-muted-foreground">Satisfaction Rate</p>
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-center text-muted-foreground py-8">
                No feedback data available
              </p>
            )}
          </CardContent>
        </Card>

        {/* Provider Accuracies */}
        <Card>
          <CardHeader>
            <CardTitle>AI Provider Performance</CardTitle>
            <CardDescription>Accuracy by AI provider and model</CardDescription>
          </CardHeader>
          <CardContent>
            {!providerAccuracies?.length ? (
              <p className="text-center text-muted-foreground py-8">
                No provider data available
              </p>
            ) : (
              <div className="space-y-4">
                {providerAccuracies.map((provider) => (
                  <div key={`${provider.provider}-${provider.model}`} className="p-3 rounded-lg bg-muted/50">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <p className="font-medium capitalize">{provider.provider}</p>
                        <p className="text-xs text-muted-foreground">{provider.model}</p>
                      </div>
                      <span className={`px-2 py-1 rounded text-sm font-medium ${getScoreBg(provider.accuracyRate ?? 0)} ${getScoreColor(provider.accuracyRate ?? 0)}`}>
                        {(provider.accuracyRate ?? 0).toFixed(1)}%
                      </span>
                    </div>
                    <div className="flex gap-4 text-xs text-muted-foreground">
                      <span>{provider.totalFeedback} reviews</span>
                      <span className="text-green-600">{provider.accurateCount} accurate</span>
                      <span className="text-yellow-600">{provider.partiallyAccurateCount} partial</span>
                      <span className="text-red-600">{provider.inaccurateCount} inaccurate</span>
                    </div>
                    {/* Progress bar */}
                    <div className="mt-2 h-2 bg-gray-200 rounded overflow-hidden flex">
                      <div
                        className="bg-green-500"
                        style={{ width: `${(provider.accurateCount / provider.totalFeedback) * 100}%` }}
                      />
                      <div
                        className="bg-yellow-500"
                        style={{ width: `${(provider.partiallyAccurateCount / provider.totalFeedback) * 100}%` }}
                      />
                      <div
                        className="bg-red-500"
                        style={{ width: `${(provider.inaccurateCount / provider.totalFeedback) * 100}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Top Performers */}
      {overview?.topPerformers && overview.topPerformers.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Trophy className="h-5 w-5 text-yellow-500" />
              Top Performers This Period
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-5">
              {overview.topPerformers.slice(0, 5).map((performer, idx) => (
                <div key={performer.userId} className="text-center p-4 bg-muted/50 rounded-lg">
                  <div className="flex justify-center mb-2">
                    {getRankIcon(idx + 1)}
                  </div>
                  <p className="font-medium truncate">{performer.displayName}</p>
                  <p className="text-xs text-muted-foreground mb-2">@{performer.username}</p>
                  <p className={`text-xl font-bold ${getScoreColor(performer.overallScore ?? 0)}`}>
                    {(performer.overallScore ?? 0).toFixed(1)}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
